import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import type { Activity } from "@/api/queries";
import { useActivity, useCompany, useTimeline, useUpdateActivity } from "@/api/queries";
import { useMe } from "@/auth/useMe";
import { CompanyTags } from "@/components/CompanyTags";
import { Dialog } from "@/components/Dialog";
import { Button, Spinner, Tag } from "@/components/ui";
import { formatDate, formatDateTime } from "@/lib/format";
import { CallBar } from "@/telephony/CallBar";
import { isCallLive, useSoftphoneStore } from "@/telephony/store";
import { Contactar } from "./Contactar";
import { CHANNEL_TAG, channelOf, isSameDay } from "./model";
import { SiguientePaso } from "./SiguientePaso";

/**
 * Screen 05 of the Modernist design — one event, opened from the agenda.
 *
 * The row it renders is already in the grid's hand, so it is passed in rather
 * than re-fetched; `useActivity` refreshes it afterwards so a completion made
 * here and a completion made in another tab agree. What is *not* in hand is
 * the rest of the company's timeline, which is the *Actividades relacionadas*
 * block, and that is one request scoped to the company — and the company's
 * tags (*No contactar*, *Grabación avisada*, *Grabación rechazada*), which the
 * activity row does not carry and which this modal, where the agents work
 * their day, is the last place to miss them.
 */
export function DetalleEvento({
  event,
  onClose,
}: {
  /** The row from the grid. `null` closes the modal. */
  event: Activity | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { data: me } = useMe();
  // The question *Completado* asks on a company's follow-up, and whose answer
  // is what completes it: what happens next with it (`SiguientePaso`).
  const [askingNext, setAskingNext] = useState(false);
  const update = useUpdateActivity();
  // Whether the softphone has anything to show — a call, or why one did not
  // start — and whether that call is still running.
  const softphoneBusy = useSoftphoneStore(
    (state) => state.call !== null || state.error !== null,
  );
  const onLiveCall = useSoftphoneStore((state) => isCallLive(state.call));

  const id = event?.id ?? 0;
  const fresh = useActivity(id, event !== null);
  // The passed row until the refresh lands: opening a modal on data already on
  // screen should not show a spinner over it.
  const activity = fresh.data ?? event;

  const related = useTimeline(
    { on: "company", id: activity?.company ?? 0 },
    1,
    activity?.company != null,
  );
  const company = useCompany(activity?.company ?? 0, activity?.company != null);

  if (activity === null || activity === undefined) return null;

  // Empty on work a call or an email logged as done: it was never booked for
  // a date, and `created` is when it happened.
  const due = activity.due_at ? new Date(activity.due_at) : null;
  const channel = channelOf(activity.kind);
  const isManager = me?.is_manager ?? false;
  const overdue = !activity.is_completed && due !== null && due.getTime() < Date.now();
  const relatedRows = (related.data?.results ?? []).filter(
    (row) => row.id !== activity.id,
  );

  function patch(body: Parameters<typeof update.mutate>[0]["body"]) {
    update.mutate({ id: activity!.id, body });
  }

  /**
   * On a company, the question of what comes next — and nothing saved until
   * it is answered. Cold prospecting has no next step to ask for: done is done.
   */
  function complete() {
    if (activity!.company != null) setAskingNext(true);
    else patch({ is_completed: true });
  }

  return (
    <>
      <Dialog
        open
        onClose={onClose}
        width={720}
        kicker={
          <span className="flex flex-wrap items-center gap-2.5">
            <Tag tone={CHANNEL_TAG[channel]}>{activity.kind_display}</Tag>
            <StateMark done={activity.is_completed ?? false} />
          </span>
        }
        title={activity.company_name ?? t("agenda.noCompany")}
        footer={
          <>
            {/* The call, where the seller is. The modal is a native `<dialog>`,
                so the bar in the shell is inert behind it: without this one a
                call started from *Contactar* could not be hung up or written
                up without closing the event it is for. In the footer, so it
                stays in reach while the event is scrolled, and so the write-up
                sits right above *Completado*, which is what comes after it. */}
            {softphoneBusy ? (
              <div className="w-full">
                <CallBar />
              </div>
            ) : null}
            {/* Done work with no date has nothing to go back to: the agenda
                finds pending work by its date, and reopened it would be on
                nobody's. The server refuses it; this does not offer it. */}
            {activity.is_completed && due === null ? null : (
              <Button
                variant={activity.is_completed ? "secondary" : "primary"}
                // Not mid-call: completing asks what comes next in a dialog over
                // this one, and it would cover the way to hang up.
                disabled={update.isPending || (onLiveCall && !activity.is_completed)}
                title={
                  onLiveCall && !activity.is_completed
                    ? t("evento.finishCallFirst")
                    : undefined
                }
                // Reopening asks nothing; completing asks what comes next.
                onClick={() =>
                  activity.is_completed ? patch({ is_completed: false }) : complete()
                }
              >
                {update.isPending
                  ? t("evento.saving")
                  : t(activity.is_completed ? "evento.markPending" : "evento.markDone")}
              </Button>
            )}
            <Button variant="ghost" className="ml-auto" onClick={onClose}>
              {t("common.close")}
            </Button>
          </>
        }
      >
        {/* First thing under the name, before the date: these change what the
            agent says on the call, or whether there is a call at all. */}
        {company.data ? (
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <CompanyTags company={company.data} />
          </div>
        ) : null}
        <p className="mb-5 text-[13px] text-mute-600">
          {formatDateTime(activity.due_at ?? activity.created)}
        </p>

        <dl className="grid gap-x-7 gap-y-5 sm:grid-cols-2">
          <Datum label={t("evento.kind")}>{activity.kind_display}</Datum>
          <Datum label={t("evento.status")}>
            {activity.is_completed
              ? t("agenda.done")
              : `${t("agenda.pending")}${
                  overdue
                    ? ` · ${t("evento.overdue")}`
                    : due !== null && isSameDay(due, new Date())
                      ? ` · ${t("evento.dueToday")}`
                      : ""
                }`}
          </Datum>

          <Datum label={t("evento.prospect")}>
            {activity.company ? (
              <Link to={`/prospectos/${activity.company}`} className="underline">
                {activity.company_name}
              </Link>
            ) : (
              // Cold prospecting: the model permits it and the agenda has to
              // render it rather than dropping the row.
              t("agenda.noCompany")
            )}
            {activity.contact_name ? (
              <div className="text-xs text-mute-600">{activity.contact_name}</div>
            ) : null}
          </Datum>

          {/* Manager-only, and labelled as such — the same rule and the same
              tag the design uses everywhere it shows somebody else's name. */}
          {isManager ? (
            <Datum label={t("evento.owner")}>
              <span className="flex flex-wrap items-center gap-2">
                {activity.current_agent_name ?? t("common.none")}
                <Tag
                  tone="outline"
                  className="text-[10px] tracking-[0.08em] uppercase"
                  title={t("agenda.managerOnly")}
                >
                  {t("agenda.manager")}
                </Tag>
              </span>
              {activity.assigned_to_name ? (
                <div className="text-xs text-mute-600">
                  {t("evento.assignedTo")}: {activity.assigned_to_name}
                </div>
              ) : null}
            </Datum>
          ) : null}
        </dl>

        <section className="mt-6 border-t border-rule pt-5">
          <p className="kicker mb-2 text-mute-600">{t("evento.comment")}</p>
          <p className="text-sm whitespace-pre-line">
            {activity.comment || t("evento.noComment")}
          </p>
        </section>

        {/* Pending work only: the ways to do it. Done work is read here — from
         *Actividades* — and has nothing left to be done from it. */}
        {activity.is_completed ? null : <Contactar activity={activity} />}

        {activity.company ? (
          <section className="mt-6 border-t border-rule pt-5">
            <div className="mb-3 flex flex-wrap items-baseline justify-between gap-3">
              <p className="kicker text-mute-600">{t("evento.related")}</p>
              <span className="text-xs text-mute-600">
                {t("evento.relatedCount", { count: relatedRows.length })}
              </span>
            </div>
            {related.isPending ? (
              <Spinner label={t("common.loading")} />
            ) : relatedRows.length === 0 ? (
              <p className="text-[13px] text-mute-600">{t("evento.noRelated")}</p>
            ) : (
              <ul>
                {relatedRows.slice(0, 6).map((row) => (
                  <li
                    key={row.id}
                    className="grid grid-cols-[88px_1fr_auto] items-center gap-3.5 border-t border-rule py-2.5 text-[13px]"
                  >
                    <span className="text-mute-600">
                      {formatDate(row.due_at ?? row.created)}
                    </span>
                    <span>
                      {row.kind_display}
                      {row.comment ? ` — ${row.comment}` : ""}
                    </span>
                    <StateMark done={row.is_completed ?? false} />
                  </li>
                ))}
              </ul>
            )}
          </section>
        ) : null}
      </Dialog>

      {askingNext ? (
        <SiguientePaso activity={activity} onClose={() => setAskingNext(false)} />
      ) : null}
    </>
  );
}

/**
 * The tick or the empty box, which is the whole of the state legend.
 *
 * A square with a border rather than a colour fill: the palette is mono and
 * the accent is spoken for, so *done* is the only one that gets it.
 */
function StateMark({ done }: { done: boolean }) {
  const { t } = useTranslation();
  const label = t(done ? "agenda.done" : "agenda.pending");
  return (
    <span
      title={label}
      aria-label={label}
      role="img"
      className={`inline-flex h-[15px] w-[15px] shrink-0 items-center justify-center border-2 text-[9px] leading-none ${
        done ? "border-brand-700 text-brand-700" : "border-mute-600"
      }`}
    >
      {done ? "✓" : ""}
    </span>
  );
}

function Datum({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="kicker mb-1 text-mute-600">{label}</dt>
      <dd className="text-sm break-words">{children}</dd>
    </div>
  );
}
